import { createApi } from '@reduxjs/toolkit/query/react';
import {
  ApplicationUser,
  ConfirmAccountRequest,
  NewPasswordRequest,
  RegistrationRequest,
  ResendConfirmationRequest,
  ResendTwoFactorCodeRequest,
  ResetPasswordRequest,
  SetupAuthenticatorRequest,
  User,
  UserRoleRequest,
  VerifyTwoFactorCodeRequest,
  VerifyTwoFactorCodeResponse
} from '../../types/users';
import {
  ServiceCodeResponse,
  ServiceConfigurationResponse,
  SubmissionDetail,
  WebSummaryRequest
} from '../../types/organisation';
import { DataError } from '../../types/system';
import {
  QuestionnaireResponse,
  SaveLocationRequest,
  SaveResponsesRequest
} from '../../types/questionnaire';
import { baseQueryWithUnauthLogout } from './apiConfig';

export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithUnauthLogout,
  endpoints: builder => ({
    // Account endpoints
    addUserToRole: builder.mutation<void, UserRoleRequest>({
      query(body) {
        return {
          url: '/accounts/addusertorole',
          method: 'POST',
          body
        };
      }
    }),
    getAdminUsers: builder.query<Array<ApplicationUser>, void>({
      query: () => '/accounts/getadminusers'
    }),
    register: builder.mutation<User | DataError, RegistrationRequest>({
      query(body) {
        return {
          url: '/accounts/register',
          method: 'POST',
          body
        };
      }
    }),
    login: builder.mutation<User | DataError, Partial<User>>({
      query(body) {
        return {
          url: '/accounts/login',
          method: 'POST',
          body
        };
      }
    }),
    verifyTwoFactorCode: builder.mutation<
      VerifyTwoFactorCodeResponse | DataError,
      VerifyTwoFactorCodeRequest
    >({
      query(body) {
        return {
          url: '/accounts/verifytwofactor',
          method: 'POST',
          body
        };
      }
    }),
    resendTwoFactorCode: builder.mutation<
      undefined | DataError,
      ResendTwoFactorCodeRequest
    >({
      query(body) {
        return {
          url: '/accounts/resendtwofactor',
          method: 'POST',
          body
        };
      }
    }),
    sendAuthenticatorSetupEmail: builder.mutation<
      undefined | DataError,
      SetupAuthenticatorRequest
    >({
      query(body) {
        return {
          url: '/accounts/sendauthenticatorsetupemail',
          method: 'POST',
          body
        };
      }
    }),
    sendAuthenticatorSetupEmailWithPdf: builder.mutation<
      undefined | DataError,
      SetupAuthenticatorRequest
    >({
      query(body) {
        return {
          url: '/accounts/sendauthenticatorsetupemailwithpdf',
          method: 'POST',
          body
        };
      }
    }),
    confirmAccount: builder.mutation<void, ConfirmAccountRequest>({
      query(body) {
        return {
          url: '/accounts/confirmaccount',
          method: 'POST',
          body
        };
      }
    }),
    resendConfirmation: builder.mutation<void, ResendConfirmationRequest>({
      query(body) {
        return {
          url: '/accounts/resendconfirmemail',
          method: 'POST',
          body
        };
      }
    }),
    resetPassword: builder.mutation<void, ResetPasswordRequest>({
      query(body) {
        return {
          url: '/accounts/resetpassword',
          method: 'POST',
          body
        };
      }
    }),
    newPassword: builder.mutation<void, NewPasswordRequest>({
      query(body) {
        return {
          url: '/accounts/newpassword',
          method: 'POST',
          body
        };
      }
    }),

    // Organisation endpoints
    getAllServiceCodes: builder.query<Array<ServiceCodeResponse>, void>({
      query: () => '/organisation/getservicecodes'
    }),
    getServiceCode: builder.query<ServiceCodeResponse, string>({
      query: code => `/organisation/getbyservicecode?servicecode=${code}`
    }),
    getServiceConfiguration: builder.query<ServiceConfigurationResponse, string>({
      query: code => `/organisation/getserviceconfiguration?servicecode=${code}`
    }),

    // Questionnaire endpoints
    getQuestionnaire: builder.query<QuestionnaireResponse, number>({
      query: id => `/questionnaire/${id}`
    }),
    saveResponses: builder.mutation<void, SaveResponsesRequest>({
      query(body) {
        return {
          url: '/questionnaire/insertorupdateresponses',
          method: 'POST',
          body
        };
      }
    }),
    saveLocation: builder.mutation<void, SaveLocationRequest>({
      query(body) {
        return {
          url: '/questionnaire/insertorupdatelocation',
          method: 'POST',
          body
        };
      }
    }),

    // Results endpoints
    submissionDetails: builder.query<Array<SubmissionDetail>, void>({
      query: () => '/results/submissiondetails'
    }),
    webSummary: builder.mutation<string, WebSummaryRequest>({
      query(body) {
        return {
          url: '/results/websummary',
          method: 'POST',
          body
        };
      }
    }),
    submissionDetailsByServiceCode: builder.query<
      Array<SubmissionDetail>,
      string
    >({
      query: code =>
        `/results/submissiondetailsbyservicecode?servicecode=${code}`
    })
  })
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  endpoints,
  useLoginMutation,
  useGetAllServiceCodesQuery,
  useLazyGetQuestionnaireQuery,
  useGetServiceCodeQuery,
  useLazyGetServiceCodeQuery,
  useGetServiceConfigurationQuery,
  useLazyGetServiceConfigurationQuery
} = api;
